import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import api from '../services/api';
import pushService from '../services/pushNotification';

// Notification sound using Web Audio API - generates clean notification chime
let audioCtx = null;

function getAudioContext() {
  if (!audioCtx) {
    audioCtx = new (window.AudioContext || window.webkitAudioContext)();
  }
  if (audioCtx.state === 'suspended') {
    audioCtx.resume();
  }
  return audioCtx;
}

function playNotificationSound() {
  try {
    const ctx = getAudioContext();
    const now = ctx.currentTime;
    const playTone = (freq, start, duration) => {
      const osc = ctx.createOscillator();
      const gain = ctx.createGain();
      osc.type = 'sine';
      osc.frequency.value = freq;
      gain.gain.setValueAtTime(0.3, start);
      gain.gain.exponentialRampToValueAtTime(0.001, start + duration);
      osc.connect(gain);
      gain.connect(ctx.destination);
      osc.start(start);
      osc.stop(start + duration);
    };
    playTone(880, now, 0.15);
    playTone(1174.66, now + 0.12, 0.2);
  } catch {
    // Audio not supported
  }
}

/** Vibrate device when new notification arrives */
function vibrateDevice(type) {
  if (!('vibrate' in navigator)) return;
  try {
    if (type === 'warning' || type === 'error') {
      navigator.vibrate([200, 100, 200, 100, 200]);
    } else {
      navigator.vibrate([200, 100, 200]);
    }
  } catch { /* non-fatal */ }
}

/** Update PWA app badge (count on app icon) */
function updateAppBadge(count) {
  if (!('setAppBadge' in navigator)) return;
  try {
    if (count > 0) {
      navigator.setAppBadge(count).catch(() => {});
    } else {
      navigator.clearAppBadge().catch(() => {});
    }
  } catch { /* non-fatal */ }
}

export const useNotificationStore = create(
  persist(
    (set, get) => ({
      // State
      notifications: [],
      unreadCount: 0,
      isLoading: false,
      isPanelOpen: false,
      settings: null,
      lastFetchedAt: null,
      pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
      pushPermission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
      pushInitialized: false,
      // PWA install state
      pwaCanInstall: false,
      pwaIsInstalled: false,
      showIOSInstallHint: false,

      // Initialize push notifications
      initPush: async () => {
        // Capture install prompt events
        pushService.captureInstallPrompt();

        // Listen for install prompt availability
        window.addEventListener('pwa-installable', () => {
          set({ pwaCanInstall: true });
        });
        window.addEventListener('pwa-installed', () => {
          set({ pwaCanInstall: false, pwaIsInstalled: true });
        });

        // Set initial install state
        const installState = pushService.getInstallState();
        set({
          pwaIsInstalled: installState.isInstalled,
          pwaCanInstall: installState.canInstall,
        });

        const available = await pushService.initialize();
        const permission = pushService.getPermissionState();
        set({ pushPermission: permission, pushInitialized: available });

        if (available && permission === 'granted') {
          // Re-register token on reconnect (refresh token if stale)
          await pushService.requestPermissionAndGetToken();
          // Foreground message → show native browser notification + update store
          pushService.onForegroundMessage((msg) => {
            // Store update happens via Socket.IO; this handles the case when socket disconnects
            console.log('[Push] Foreground message received');
          });
        }

        // Handle NOTIFICATION_CLICK from service worker
        if ('serviceWorker' in navigator) {
          navigator.serviceWorker.addEventListener('message', (event) => {
            if (event.data?.type === 'NOTIFICATION_CLICK') {
              // Let React Router handle navigation
              window.dispatchEvent(new CustomEvent('sw-navigate', { detail: { url: event.data.url } }));
            }
          });
        }
      },

      // Request push notification permission
      requestPushPermission: async () => {
        const token = await pushService.requestPermissionAndGetToken();
        const permission = pushService.getPermissionState();
        set({ pushPermission: permission });

        if (token) {
          pushService.onForegroundMessage((msg) => {
            console.log('[Push] Foreground message received');
          });
        }
        return !!token;
      },

      // Disable push notifications
      disablePush: async () => {
        await pushService.unregisterDevice();
        const settings = get().settings;
        if (settings) {
          await get().updateSettings({ push_enabled: false });
        }
      },

      // PWA: trigger native install prompt (Android/Chrome)
      installPWA: async () => {
        const outcome = await pushService.triggerInstallPrompt();
        if (outcome === 'accepted') {
          set({ pwaIsInstalled: true, pwaCanInstall: false });
        }
        return outcome;
      },

      // PWA: show/hide iOS install hint
      setIOSInstallHint: (visible) => set({ showIOSInstallHint: visible }),

      // Toggle panel
      togglePanel: () => set((state) => ({ isPanelOpen: !state.isPanelOpen })),
      closePanel: () => set({ isPanelOpen: false }),
      openPanel: () => set({ isPanelOpen: true }),

      // Fetch notifications
      fetchNotifications: async (page = 1, append = false) => {
        try {
          set({ isLoading: true });
          const res = await api.get('/notifications', { params: { page, limit: 20 } });
          if (res.data?.success) {
            set((state) => ({
              notifications: append
                ? [...state.notifications, ...res.data.data]
                : res.data.data,
              pagination: res.data.pagination,
              isLoading: false,
              lastFetchedAt: Date.now(),
            }));
          }
        } catch {
          set({ isLoading: false });
        }
      },

      // Receive a new real-time notification (from Socket.IO)
      handleNewNotification: (notification) => {
        const settings = get().settings;
        // Play sound if enabled
        if (settings?.sound_enabled !== false) {
          playNotificationSound();
        }
        // Vibrate device
        vibrateDevice(notification.type);

        const newCount = get().unreadCount + 1;
        set((state) => ({
          notifications: [notification, ...state.notifications],
          unreadCount: newCount,
        }));

        // Update PWA app icon badge
        updateAppBadge(newCount);

        // Show toast popup
        try {
          const { useToastStore } = require('../components/NotificationToast');
          useToastStore.getState().addToast(notification);
        } catch { /* non-fatal */ }
      },

      // Fetch unread count (fallback polling)
      fetchUnreadCount: async () => {
        try {
          const res = await api.get('/notifications/unread-count');
          if (res.data?.success) {
            const newCount = res.data.data.count;
            const oldCount = get().unreadCount;
            const settings = get().settings;

            // Play sound if new notifications arrived
            if (newCount > oldCount && oldCount >= 0 && settings?.sound_enabled !== false) {
              playNotificationSound();
            }

            set({ unreadCount: newCount });
            // Sync PWA app badge
            updateAppBadge(newCount);
          }
        } catch {
          // Silently ignore
        }
      },

      // Mark as read
      markAsRead: async (notificationId) => {
        try {
          await api.put(`/notifications/${notificationId}/read`);
          set((state) => ({
            notifications: state.notifications.map((n) =>
              n.notification_id === notificationId
                ? { ...n, is_read: true, read_at: new Date().toISOString() }
                : n
            ),
            unreadCount: Math.max(0, state.unreadCount - 1),
          }));
        } catch {
          // Silently ignore
        }
      },

      // Mark all as read
      markAllAsRead: async () => {
        try {
          await api.put('/notifications/read-all');
          set((state) => ({
            notifications: state.notifications.map((n) => ({
              ...n,
              is_read: true,
              read_at: n.read_at || new Date().toISOString(),
            })),
            unreadCount: 0,
          }));
          updateAppBadge(0);
        } catch {
          // Silently ignore
        }
      },

      // Delete notification
      deleteNotification: async (notificationId) => {
        try {
          await api.delete(`/notifications/${notificationId}`);
          set((state) => {
            const notif = state.notifications.find((n) => n.notification_id === notificationId);
            return {
              notifications: state.notifications.filter((n) => n.notification_id !== notificationId),
              unreadCount: notif && !notif.is_read ? Math.max(0, state.unreadCount - 1) : state.unreadCount,
            };
          });
        } catch {
          // Silently ignore
        }
      },

      // Delete all read
      deleteAllRead: async () => {
        try {
          await api.delete('/notifications/read');
          set((state) => ({
            notifications: state.notifications.filter((n) => !n.is_read),
          }));
        } catch {
          // Silently ignore
        }
      },

      // Xoá TẤT CẢ thông báo của tôi (đã đọc lẫn chưa đọc) — DELETE /notifications/all
      deleteAll: async () => {
        try {
          const res = await api.delete('/notifications/all');
          set((state) => ({
            notifications: [],
            unreadCount: 0,
            pagination: state.pagination ? { ...state.pagination, total: 0 } : state.pagination,
          }));
          return res.data?.data?.deleted ?? 0;
        } catch {
          return 0;
        }
      },

      // Fetch settings
      fetchSettings: async () => {
        try {
          const res = await api.get('/notifications/settings');
          if (res.data?.success) {
            set({ settings: res.data.data });
          }
        } catch {
          // Silently ignore
        }
      },

      // Update settings
      updateSettings: async (updates) => {
        try {
          const res = await api.put('/notifications/settings', updates);
          if (res.data?.success) {
            set({ settings: res.data.data });
            return true;
          }
        } catch {
          return false;
        }
      },

      // Reset store
      reset: () =>
        set({
          notifications: [],
          unreadCount: 0,
          isLoading: false,
          isPanelOpen: false,
          settings: null,
          lastFetchedAt: null,
          pagination: { total: 0, page: 1, limit: 20, totalPages: 0 },
          pushPermission: typeof Notification !== 'undefined' ? Notification.permission : 'unsupported',
          pwaCanInstall: false,
          pwaIsInstalled: false,
          showIOSInstallHint: false,
        }),
    }),
    {
      name: 'notification-storage',
      partialize: (state) => ({
        settings: state.settings,
      }),
    }
  )
);
